/**
 * Sales Dashboard AI Utilities
 * Summarize filteredData into compact payloads for AI endpoints (~2-5KB each).
 */

// Simple hash for cache keys
export const hashKey = (str) => {
  let h = 5381;
  for (let i = 0; i < str.length; i++) h = ((h << 5) + h + str.charCodeAt(i)) & 0x7fffffff;
  return h.toString(36);
};

const topNByAmount = (map, n = 20) =>
  Object.entries(map)
    .sort((a, b) => b[1].amount - a[1].amount)
    .slice(0, n)
    .map(([k, v]) => ({ name: k, qty: Math.round(v.qty), amount: Math.round(v.amount * 100) / 100, count: v.count || 0 }));

const topNByQty = (map, n = 20) =>
  Object.entries(map)
    .sort((a, b) => b[1].qty - a[1].qty)
    .slice(0, n)
    .map(([k, v]) => ({ name: k, qty: Math.round(v.qty), amount: Math.round(v.amount * 100) / 100, count: v.count || 0 }));

// Keep backward compat
const topN = topNByAmount;

const dayStr = (d) => {
  if (!d) return null;
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  return String(d).slice(0, 10);
};

/**
 * Build summary for AI Insights & Anomaly Detection
 */
export function buildInsightsSummary(rows, getItemInfo, dateFrom, dateTo) {
  const skuMap = {}, custMap = {}, collMap = {}, stateMap = {}, whsMap = {}, dailyMap = {};
  let totalQty = 0, totalAmt = 0;

  rows.forEach(r => {
    const qty = Number(r.qty) || 0;
    const amt = Number(r.amount) || 0;
    totalQty += qty;
    totalAmt += amt;

    // SKU
    const sku = r.itemId || 'Unknown';
    if (!skuMap[sku]) skuMap[sku] = { qty: 0, amount: 0 };
    skuMap[sku].qty += qty; skuMap[sku].amount += amt;

    // Customer
    const cust = r.customer || 'Unknown';
    if (!custMap[cust]) custMap[cust] = { qty: 0, amount: 0 };
    custMap[cust].qty += qty; custMap[cust].amount += amt;

    // Collection
    const info = getItemInfo ? getItemInfo(sku) : null;
    const coll = info?.collection || 'Unknown';
    if (!collMap[coll]) collMap[coll] = { qty: 0, amount: 0 };
    collMap[coll].qty += qty; collMap[coll].amount += amt;

    // State
    const st = r.state || 'Unknown';
    if (!stateMap[st]) stateMap[st] = { qty: 0, amount: 0 };
    stateMap[st].qty += qty; stateMap[st].amount += amt;

    // Warehouse
    const whs = r.whs || 'Unknown';
    if (!whsMap[whs]) whsMap[whs] = { qty: 0, amount: 0 };
    whsMap[whs].qty += qty; whsMap[whs].amount += amt;

    // Daily
    const day = dayStr(r.shipDate);
    if (day) {
      if (!dailyMap[day]) dailyMap[day] = { qty: 0, amount: 0 };
      dailyMap[day].qty += qty; dailyMap[day].amount += amt;
    }
  });

  const dailyTrend = Object.entries(dailyMap).sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, v]) => ({ date, qty: Math.round(v.qty), amount: Math.round(v.amount) }));

  return {
    dateRange: `${dateFrom} to ${dateTo}`,
    totalOrders: rows.length,
    totalQty: Math.round(totalQty),
    totalAmount: Math.round(totalAmt),
    topSKUs: topN(skuMap, 20),
    topCustomers: topN(custMap, 20),
    topCollections: topN(collMap, 10),
    topStates: topN(stateMap, 10),
    topWarehouses: topN(whsMap, 10),
    dailyTrend,
  };
}

/**
 * Build summary for Demand Forecasting
 */
export function buildForecastSummary(rows, getItemInfo, granularity = 'overall', selectedItem = '') {
  const dailyMap = {};

  rows.forEach(r => {
    if (granularity === 'sku' && selectedItem && r.itemId !== selectedItem) return;
    if (granularity === 'collection' && selectedItem) {
      const info = getItemInfo ? getItemInfo(r.itemId) : null;
      if (info?.collection !== selectedItem) return;
    }

    const day = dayStr(r.shipDate);
    if (!day) return;
    if (!dailyMap[day]) dailyMap[day] = { qty: 0, amount: 0, orders: 0 };
    dailyMap[day].qty += Number(r.qty) || 0;
    dailyMap[day].amount += Number(r.amount) || 0;
    dailyMap[day].orders += 1;
  });

  return {
    granularity,
    selectedItem: selectedItem || 'all',
    dailyHistory: Object.entries(dailyMap).sort((a, b) => a[0].localeCompare(b[0]))
      .map(([date, v]) => ({ date, qty: Math.round(v.qty), amount: Math.round(v.amount), orders: v.orders })),
  };
}

/**
 * Build summary for Customer Segmentation & Churn
 */
export function buildCustomerSummary(rows, dateFrom, dateTo) {
  const custMap = {};

  rows.forEach(r => {
    const cust = r.customer;
    if (!cust) return;
    if (!custMap[cust]) custMap[cust] = { lastOrder: null, orderDates: new Set(), totalAmt: 0, totalQty: 0, skus: new Set() };
    const d = dayStr(r.shipDate);
    if (d) {
      custMap[cust].orderDates.add(d);
      if (!custMap[cust].lastOrder || d > custMap[cust].lastOrder) custMap[cust].lastOrder = d;
    }
    custMap[cust].totalAmt += Number(r.amount) || 0;
    custMap[cust].totalQty += Number(r.qty) || 0;
    if (r.itemId) custMap[cust].skus.add(r.itemId);
  });

  return {
    dateRange: `${dateFrom} to ${dateTo}`,
    currentDate: new Date().toISOString().slice(0, 10),
    customers: Object.entries(custMap)
      .sort((a, b) => b[1].totalAmt - a[1].totalAmt)
      .slice(0, 30)
      .map(([name, v]) => ({
        name,
        lastOrderDate: v.lastOrder,
        orderCount: v.orderDates.size,
        totalAmount: Math.round(v.totalAmt),
        uniqueSKUs: v.skus.size,
        avgOrderValue: v.orderDates.size > 0 ? Math.round(v.totalAmt / v.orderDates.size) : 0,
      })),
  };
}

/**
 * Build context for Natural Language Query
 * Includes both amount-sorted AND qty-sorted lists so AI can answer either type of question
 */
export function buildQueryContext(rows, getItemInfo, dateFrom, dateTo) {
  const skuMap = {}, custMap = {}, collMap = {}, stateMap = {}, whsMap = {};
  let totalQty = 0, totalAmt = 0;

  rows.forEach(r => {
    const qty = Number(r.qty) || 0;
    const amt = Number(r.amount) || 0;
    totalQty += qty; totalAmt += amt;

    const sku = r.itemId || 'Unknown';
    if (!skuMap[sku]) skuMap[sku] = { qty: 0, amount: 0 };
    skuMap[sku].qty += qty; skuMap[sku].amount += amt;

    const cust = r.customer || 'Unknown';
    if (!custMap[cust]) custMap[cust] = { qty: 0, amount: 0 };
    custMap[cust].qty += qty; custMap[cust].amount += amt;

    const info = getItemInfo ? getItemInfo(sku) : null;
    const coll = info?.collection || 'Unknown';
    if (!collMap[coll]) collMap[coll] = { qty: 0, amount: 0 };
    collMap[coll].qty += qty; collMap[coll].amount += amt;

    const st = r.state || 'Unknown';
    if (!stateMap[st]) stateMap[st] = { qty: 0, amount: 0 };
    stateMap[st].qty += qty; stateMap[st].amount += amt;

    const whs = r.whs || 'Unknown';
    if (!whsMap[whs]) whsMap[whs] = { qty: 0, amount: 0 };
    whsMap[whs].qty += qty; whsMap[whs].amount += amt;
  });

  // Daily breakdown
  const dailyMap = {};
  rows.forEach(r => {
    const day = dayStr(r.shipDate);
    if (!day) return;
    if (!dailyMap[day]) dailyMap[day] = { qty: 0, amount: 0, orders: 0 };
    dailyMap[day].qty += Number(r.qty) || 0;
    dailyMap[day].amount += Number(r.amount) || 0;
    dailyMap[day].orders += 1;
  });
  const dailySales = Object.entries(dailyMap).sort((a, b) => a[0].localeCompare(b[0]))
    .map(([date, v]) => ({ date, qty: Math.round(v.qty), amount: Math.round(v.amount), orders: v.orders }));

  return {
    dateRange: `${dateFrom} to ${dateTo}`,
    totalOrders: rows.length,
    totalQty: Math.round(totalQty),
    totalAmount: Math.round(totalAmt),
    schema: 'Each order has: customer, shipDate, qty, amount, itemId (SKU), whs (warehouse code), zip, state. Item metadata: collection, color, size, style. IMPORTANT: Use topSKUsByQty for quantity questions, topSKUsByAmount for revenue questions. dailySales has per-day breakdown with date, qty, amount, orders - use this for any date-specific or daily trend questions.',
    dailySales,
    topSKUsByAmount: topNByAmount(skuMap, 15),
    topSKUsByQty: topNByQty(skuMap, 15),
    topCustomersByAmount: topNByAmount(custMap, 10),
    topCustomersByQty: topNByQty(custMap, 10),
    topCollections: topNByAmount(collMap, 10),
    topStates: topNByAmount(stateMap, 10),
    topWarehouses: topNByAmount(whsMap, 10),
  };
}

/**
 * Build context for Smart Recommendations
 */
export function buildRecommendationContext(rows, getItemInfo, mode, selected) {
  if (mode === 'sku') {
    // Find customers who bought this SKU, then what else they bought
    const buyersOfSelected = new Set();
    rows.forEach(r => { if (r.itemId === selected) buyersOfSelected.add(r.customer); });

    const coPurchaseMap = {};
    rows.forEach(r => {
      if (buyersOfSelected.has(r.customer) && r.itemId !== selected) {
        if (!coPurchaseMap[r.itemId]) coPurchaseMap[r.itemId] = { count: 0, customers: new Set() };
        coPurchaseMap[r.itemId].customers.add(r.customer);
        coPurchaseMap[r.itemId].count += 1;
      }
    });

    const info = getItemInfo ? getItemInfo(selected) : {};
    return {
      mode: 'sku',
      selectedItem: selected,
      itemMetadata: info ? `Collection: ${info.collection || '?'}, Color: ${info.color || '?'}, Size: ${info.size || '?'}` : '',
      buyerCount: buyersOfSelected.size,
      coPurchases: Object.entries(coPurchaseMap)
        .sort((a, b) => b[1].customers.size - a[1].customers.size)
        .slice(0, 20)
        .map(([item, v]) => ({ item, coCustomers: v.customers.size, coOrders: v.count })),
    };
  }

  if (mode === 'customer') {
    const items = {};
    rows.forEach(r => {
      if (r.customer === selected) {
        if (!items[r.itemId]) items[r.itemId] = { qty: 0, amount: 0 };
        items[r.itemId].qty += Number(r.qty) || 0;
        items[r.itemId].amount += Number(r.amount) || 0;
      }
    });
    return {
      mode: 'customer',
      selectedCustomer: selected,
      purchasedItems: Object.entries(items)
        .sort((a, b) => b[1].amount - a[1].amount)
        .slice(0, 30)
        .map(([item, v]) => {
          const info = getItemInfo ? getItemInfo(item) : {};
          return { item, collection: info?.collection || '?', qty: Math.round(v.qty), amount: Math.round(v.amount) };
        }),
    };
  }

  return { mode, selected };
}
